import React, { useState, useEffect } from 'react';
import { getAllProducts, updateProduct, deleteProduct, createProduct } from '../api/products';
import './ucUsers.css';

const UcProducts1 = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState('');
  const [formData, setFormData] = useState({
    prodName: '',
    prodDesc: '',
    prodPrice: '',
    prodQuantity: '',
    lucro: '',
    prodBuyPrice: '',
    color: '',
    pCode: '',
    inactive: false,
    barCode: '',
    supplierId: '',
    modelId: '',
    username: '',
    shopId: '',
    photo: ''
  });

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    const response = await getAllProducts();
    if (response && Array.isArray(response.products)) {
      setProducts(response.products);
      setFilteredProducts(response.products);
      setError(null);
    } else {
      setError('Failed to fetch products');
      // Set mock data for demo if available
      // setUsers(mockUsers);
      // setFilteredUsers(mockUsers);
    }
    setLoading(false);
  };

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = products;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        product =>
          product.prodName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.prodDesc?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.pCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product._id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(
        product => (product.role || product.isAdmin ? 'admin' : 'user') === filterRole
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(
        product => (product.status || (product.isActive ? 'active' : 'inactive')) === filterStatus
      );
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [products, searchTerm, filterRole, filterStatus]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      const response = await deleteProduct(productId);
      if (response.success) {
        setProducts(products.filter(p => p._id !== productId));
      } else {
        alert('Failed to delete product');
      }
    }
  };

  const handleEditClick = (product) => {
    setEditingProductId(product._id || product.id);
    setFormData({
      prodName: product.prodName || '',
      prodDesc: product.prodDesc || '',
      prodPrice: product.prodPrice ?? '',
      prodQuantity: product.prodQuantity ?? '',
      lucro: product.lucro ?? '',
      prodBuyPrice: product.prodBuyPrice ?? '',
      color: product.color || '',
      pCode: product.pCode || '',
      inactive: Boolean(product.inactive),
      barCode: product.barCode || '',
      supplierId: product.supplierId ?? '',
      modelId: product.modelId ?? '',
      username: product.username || '',
      shopId: product.shopId ?? '',
      photo: product.photo || ''
    });

    if (product.photo) {
      setPhotoPreview(product.photo.startsWith('data:') ? product.photo : `data:image/png;base64,${product.photo}`);
    } else {
      setPhotoPreview('');
    }

    setShowAddForm(true);
  };

  const resetForm = () => {
    setEditingProductId(null);
    setFormData({
      prodName: '',
      prodDesc: '',
      prodPrice: '',
      prodQuantity: '',
      lucro: '',
      prodBuyPrice: '',
      color: '',
      pCode: '',
      inactive: false,
      barCode: '',
      supplierId: '',
      modelId: '',
      username: '',
      shopId: '',
      photo: ''
    });
    setPhotoPreview('');
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1024; // reduce large images to at most 1024px
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Prefer JPEG to reduce payload for photos; keep PNG if original is PNG
        const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const quality = mime === 'image/png' ? 0.92 : 0.8;
        const dataUrl = canvas.toDataURL(mime, quality);
        const base64 = dataUrl.split(',')[1] || '';

        setFormData(prev => ({ ...prev, photo: base64 }));
        setPhotoPreview(dataUrl);
      };
      img.onerror = () => {
        // fallback to raw file preview
        const dataUrl = e.target.result;
        const base64 = dataUrl.split(',')[1] || '';
        setFormData(prev => ({ ...prev, photo: base64 }));
        setPhotoPreview(dataUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleAddProduct = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const payload = {
      ...formData,
      prodPrice: formData.prodPrice ? Number(formData.prodPrice) : 0,
      prodQuantity: formData.prodQuantity ? Number(formData.prodQuantity) : 0,
      lucro: formData.lucro ? Number(formData.lucro) : 0,
      prodBuyPrice: formData.prodBuyPrice ? Number(formData.prodBuyPrice) : 0,
      supplierId: formData.supplierId ? Number(formData.supplierId) : 0,
      modelId: formData.modelId ? Number(formData.modelId) : 0,
      shopId: formData.shopId ? Number(formData.shopId) : 0,
      inactive: Boolean(formData.inactive)
    };

    const response = await createProduct(payload);

    if (response && response.product) {
      const createdProduct = {
        ...response.product,
        photo: response.product.photo || formData.photo
      };
      setProducts(prev => [createdProduct, ...prev]);
      setFilteredProducts(prev => [createdProduct, ...prev]);
      setShowAddForm(false);
      resetForm();
      setError(null);
    } else {
      alert(response?.message || 'Failed to create product');
    }

    setSubmitting(false);
  };

  const handleEditProduct = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    if (!editingProductId) {
      alert('No product selected for editing.');
      setSubmitting(false);
      return;
    }

    const payload = {
      ...formData,
      prodPrice: formData.prodPrice ? Number(formData.prodPrice) : 0,
      prodQuantity: formData.prodQuantity ? Number(formData.prodQuantity) : 0,
      lucro: formData.lucro ? Number(formData.lucro) : 0,
      prodBuyPrice: formData.prodBuyPrice ? Number(formData.prodBuyPrice) : 0,
      supplierId: formData.supplierId ? Number(formData.supplierId) : 0,
      modelId: formData.modelId ? Number(formData.modelId) : 0,
      shopId: formData.shopId ? Number(formData.shopId) : 0,
      inactive: Boolean(formData.inactive)
    };

    const response = await updateProduct(editingProductId, payload);

    if (response && response.product) {
      const updatedProduct = {
        ...response.product,
        photo: response.product.photo || formData.photo
      };

      setProducts(prev => prev.map(p => (p._id === editingProductId || p.id === editingProductId ? updatedProduct : p)));
      setFilteredProducts(prev => prev.map(p => (p._id === editingProductId || p.id === editingProductId ? updatedProduct : p)));
      setShowAddForm(false);
      resetForm();
      setError(null);
    } else {
      alert(response?.message || 'Failed to update product');
    }

    setSubmitting(false);
  };

  return (
    <div className="users-container">
      <div className="users-header">
        <div className="users-title">
          <h1>Product Management</h1>
          <p>Create and manage products, prices, stock, colors, supplier details, and product images from one place.</p>
        </div>
      </div>

      <div className="users-toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">🏠 Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            {/*<option value="banned">Banned</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>*/}
          </select>

        </div>

        <div className="action-buttons">
          <button className="btn-export">📥 Export</button>
          <button className="btn-add-user" onClick={() => { resetForm(); setShowAddForm(true); }}>+ Add Product</button>
        </div>
      </div>

      {showAddForm && (
        <div className="product-modal-overlay">
          <div className="product-modal">
            <div className="product-modal-header">
              <h3>{editingProductId ? 'Edit Product' : 'Add New Product'}</h3>
              <button type="button" className="product-modal-close" onClick={() => { setShowAddForm(false); resetForm(); }}>
                ×
              </button>
            </div>

            <form className="product-form" onSubmit={editingProductId ? handleEditProduct : handleAddProduct}>
              <div className="product-form-grid">
                <label>
                  Product Name
                  <input type="text" name="prodName" value={formData.prodName} onChange={handleFormChange} required />
                </label>
                <label>
                  Code
                  <input type="text" name="pCode" value={formData.pCode} onChange={handleFormChange} />
                </label>
                <label>
                  Price
                  <input type="number" name="prodPrice" value={formData.prodPrice} onChange={handleFormChange} step="0.01" required />
                </label>
                <label>
                  Buy Price
                  <input type="number" name="prodBuyPrice" value={formData.prodBuyPrice} onChange={handleFormChange} step="0.01" />
                </label>
                <label>
                  Quantity
                  <input type="number" name="prodQuantity" value={formData.prodQuantity} onChange={handleFormChange} min="0" required />
                </label>
                <label>
                  Profit %
                  <input type="number" name="lucro" value={formData.lucro} onChange={handleFormChange} step="0.01" />
                </label>
                <label>
                  Color
                  <input type="text" name="color" value={formData.color} onChange={handleFormChange} />
                </label>
                <label>
                  Barcode
                  <input type="text" name="barCode" value={formData.barCode} onChange={handleFormChange} />
                </label>
                <label>
                  Supplier ID
                  <input type="number" name="supplierId" value={formData.supplierId} onChange={handleFormChange} min="0" />
                </label>
                <label>
                  Model ID
                  <input type="number" name="modelId" value={formData.modelId} onChange={handleFormChange} min="0" />
                </label>
                <label>
                  Username
                  <input type="text" name="username" value={formData.username} onChange={handleFormChange} />
                </label>
                <label>
                  Shop ID
                  <input type="number" name="shopId" value={formData.shopId} onChange={handleFormChange} min="0" />
                </label>
              </div>

              <label className="product-form-full">
                Description
                <textarea name="prodDesc" value={formData.prodDesc} onChange={handleFormChange} rows="3" />
              </label>

              <label className="product-form-full">
                Product Photo
                <input type="file" accept="image/*" onChange={handlePhotoChange} />
              </label>

              {photoPreview && (
                <div className="product-photo-preview">
                  <img src={photoPreview} alt="Preview" />
                </div>
              )}

              <label className="product-form-inline">
                <input type="checkbox" name="inactive" checked={formData.inactive} onChange={handleFormChange} />
                Mark as inactive
              </label>

              <div className="product-form-actions">
                <button type="button" className="btn-export" onClick={() => { setShowAddForm(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-add-user" disabled={submitting}>
                  {submitting ? 'Saving...' : editingProductId ? 'Save Changes' : 'Save Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" />
                  </th>
                  <th>Product ⬍</th>
                  <th>Code ⬍</th>
                  <th>Model code ⬍</th>
                  <th>Color ⬍</th>
                  <th>Active ⬍</th>
                  <th>In stock ⬍</th>
                  <th>Price ⬍</th>
                  {/*<th>Actions ⬍</th>*/}
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.length > 0 ? (
                  paginatedProducts.map((product) => (
                    <tr key={product._id || product.id}>
                      <td>
                        <input type="checkbox" />
                      </td>
                      <td className="user-name-cell">
                        <img
                          src={product.photo ? `data:image/png;base64,${product.photo}` : `https://via.placeholder.com/32?text=${product.prodName?.charAt(0) || 'U'}`}
                          alt={product.prodName}
                          className="user-avatar"
                        />
                        <span>{product.prodName}</span>
                      </td>
                      <td style={{ color: '#333333' }}>{product.pCode || '-'}</td>
                      <td style={{ color: '#333333' }}>{product.modelId ?? '-'}</td>
                      <td style={{ color: '#333333' }}>{product.color || '-'}</td>
                      <td style={{ color: '#333333' }}>{product.inactive ? 'Inactive' : 'Active'}</td>
                      <td style={{ color: '#333333' }}>{product.prodQuantity ?? 0}</td>
                      <td style={{ color: '#333333', fontWeight: 'bold', fontSize: '18px'}}>{product.prodPrice ?? 0}</td>
                      {/*<td className="actions-cell">
                         <button className="action-btn edit-btn" title="Edit" onClick={() => handleEditClick(product)}>
                          ✏️
                        </button> 
                        <button
                          className="action-btn delete-btn"
                          title="Delete"
                          onClick={() => handleDeleteProduct(product._id || product.id)}
                        >
                          🗑️
                        </button>
                      </td>*/}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="no-data">
                      Not found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <div className="rows-per-page">
              <span>Rows per page</span>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>of {filteredProducts.length} rows</span>
            </div>

            <div className="pagination-controls">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                ⟨⟨
              </button>
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
                ⟨
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={currentPage === pageNum ? 'active' : ''}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span>...</span>
                  <button onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
                </>
              )}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                ⟩
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                ⟩⟩
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UcProducts1;

